'use client';

import { ColumnDef } from '@tanstack/react-table';
import { Tag, DollarSign, Ellipsis, Calendar } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header';
import { DataTableCheckbox } from '@/components/data-table/data-table-checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PromotionItem } from '@/lib/api/promotions';
import { shouldWarnPromotionWithoutTrigger } from '@/lib/promotion-discount';
import { DateDisplay } from '@/components/date-display';
import { useStoreNavigate } from '@/hooks/use-app-navigate';
import { PromotionResourceBadge } from '@/components/promocode/PromotionResourceBadge';

interface GetPromotionsTableColumnsProps {
    storeId: string;
    onEditPromotion: (promotionId: string) => void;
    onDeletePromotion: (promotionId: string) => void;
}

export const usePromotionsTableColumns = ({
    storeId,
    onEditPromotion,
    onDeletePromotion,
}: GetPromotionsTableColumnsProps): ColumnDef<PromotionItem>[] => {
    const { t } = useTranslation('stores');
    return React.useMemo(() => [
        {
            id: 'select',
            header: ({ table }) => (
                <Checkbox
                    checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')}
                    onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                    aria-label={t('promotionsTable.selectAll')}
                    className="translate-y-0.5"
                />
            ),
            cell: ({ row }) => (
                <DataTableCheckbox row={row} />
            ),
            enableSorting: false,
            enableHiding: false,
            size: 40,
        },
        {
            id: 'id',
            accessorKey: 'id',
            header: ({ column }) => <DataTableColumnHeader column={column} title={t('promotionsTable.columns.id')} />,
            cell: ({ row }) => {
                const promotion = row.original;
                return (
                    <PromotionResourceBadge promotionId={promotion.id} />
                );
            },
            meta: { label: t('promotionsTable.columns.id') },
            enableSorting: false,
        },
        {
            id: 'name',
            accessorKey: 'name',
            header: ({ column }) => <DataTableColumnHeader column={column} title={t('promotionsTable.columns.name')} />,
            cell: ({ row }) => {
                const promotion = row.original;
                const { storeRoutes } = useStoreNavigate();
                return (
                    <div className="min-w-0">
                        <Link to={storeRoutes.DISCOUNT_DETAILS(promotion.id)}>
                            <div className="truncate font-medium hover:underline">{promotion.name}</div>
                        </Link>
                        {promotion.code && (
                            <div className="text-muted-foreground truncate text-xs max-w-[400px]">
                                {t('promotionsTable.codePrefix', { code: promotion.code })}
                            </div>
                        )}
                    </div>
                );
            },
            meta: {
                label: t('promotionsTable.columns.name'),
                placeholder: t('promotionsTable.searchPromotions'),
                variant: 'text',
                icon: () => <Tag />,
            },
            enableColumnFilter: true,
            enableSorting: true,
        },
        {
            id: 'status',
            accessorKey: 'enabled',
            header: ({ column }) => <DataTableColumnHeader column={column} title={t('promotionsTable.columns.status')} />,
            cell: ({ row }) => {
                const promotion = row.original;
                if (!promotion.enabled) {
                    return <Badge variant="secondary">{t('promotionsTable.status.inactive')}</Badge>;
                }
                if (shouldWarnPromotionWithoutTrigger(promotion)) {
                    return <Badge variant="yellow">{t('promotionsTable.status.noTrigger')}</Badge>;
                }
                return <Badge variant="default">{t('promotionsTable.status.active')}</Badge>;
            },
            enableSorting: false,
            meta: { label: t('promotionsTable.columns.status') },
        },
        {
            id: 'dates',
            accessorKey: 'startDate',
            header: ({ column }) => <DataTableColumnHeader column={column} title={t('promotionsTable.columns.period')} />,
            cell: ({ row }) => {
                const promotion = row.original;
                if (!promotion.startDate && !promotion.endDate) {
                    return <span className="text-muted-foreground">{t('promotionsTable.period.noLimit')}</span>;
                }
                return (
                    <div className="text-sm">
                        {promotion.startDate && (
                            <div className="flex flex-wrap items-baseline gap-1">
                                <span>{t('promotionsTable.period.fromLabel')}</span>
                                <DateDisplay value={promotion.startDate} mode="date" />
                            </div>
                        )}
                        {promotion.endDate && (
                            <div className="flex flex-wrap items-baseline gap-1">
                                <span>{t('promotionsTable.period.toLabel')}</span>
                                <DateDisplay value={promotion.endDate} mode="date" />
                            </div>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            meta: { label: t('promotionsTable.columns.period') },
        },
        {
            id: 'usage',
            accessorKey: 'usageCount',
            header: ({ column }) => <DataTableColumnHeader column={column} title={t('promotionsTable.columns.usage')} />,
            cell: ({ row }) => {
                const promotion = row.original;
                const usageCount = promotion.usageCount || 0;
                const usageLimit = promotion.usageLimit;

                if (!usageLimit) {
                    return (
                        <div className="text-sm">
                            <div className="font-medium">{usageCount}</div>
                            <div className="text-muted-foreground text-xs">{t('promotionsTable.usage.noLimit')}</div>
                        </div>
                    );
                }

                const percentage = (usageCount / usageLimit) * 100;
                const isNearLimit = percentage >= 80;

                return (
                    <div className="text-sm">
                        <div className="font-medium">
                            {usageCount} / {usageLimit}
                        </div>
                        <div className={`text-xs ${isNearLimit ? 'text-orange-600' : 'text-muted-foreground'}`}>
                            {t('promotionsTable.usage.percentUsed', { percent: percentage.toFixed(0) })}
                        </div>
                    </div>
                );
            },
            enableSorting: true,
            meta: { label: t('promotionsTable.columns.usage') },
        },
        {
            id: 'actions',
            cell: ({ row }) => {
                const promotion = row.original;
                const { storeRoutes } = useStoreNavigate();
                return (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                aria-label={t('promotionsTable.openMenu')}
                                variant="ghost"
                                className="data-[state=open]:bg-muted flex h-8 w-8 p-0"
                            >
                                <Ellipsis className="h-4 w-4" aria-hidden="true" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-[160px]">
                            <DropdownMenuItem onClick={() => onEditPromotion(promotion.id)}>
                                {t('promotionsTable.actions.edit')}
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild>
                                <Link to={storeRoutes.DISCOUNT_DETAILS(promotion.id)}>{t('promotionsTable.actions.viewDetails')}</Link>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                onClick={() => onDeletePromotion(promotion.id)}
                                className="text-destructive focus:text-destructive"
                            >
                                {t('promotionsTable.actions.delete')}
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                );
            },
            enableSorting: false,
            enableHiding: false,
            size: 40,
        },
    ], [t, storeId, onEditPromotion, onDeletePromotion]);
};
